import { useState, useEffect, useCallback } from "react";
import { V, optInputStyle } from "../theme.js";
import { OptBtn, Modal } from "../ui/components.jsx";

const FONTS = ["Georgia","Helvetica","Courier New","Times New Roman","Verdana","Trebuchet MS","Palatino","Garamond"];
const DEFAULT_CFG = { text:"", font:"Georgia", size:36, weight:"normal", color:"#000000", align:"left", x:20, y:20 };

/**
 * TextDialog — add or update a text layer.
 *
 * Props:
 *   open            boolean
 *   onClose         () => void
 *   onCommit        (cfg) => void
 *   activeTextData  object|null  — full textData if editing existing layer
 *   seed            React ref    — {x,y} for new text position, or full textData for edit
 */
export function TextDialog({ open, onClose, onCommit, activeTextData, seed }) {
  const [cfg, setCfg] = useState(DEFAULT_CFG);

  // Apply seed when dialog opens
  useEffect(() => {
    if (!open) return;
    const s = seed?.current;
    if (!s) return;
    if (s.text != null) {
      // Full textData — editing existing layer
      setCfg({ ...DEFAULT_CFG, ...s });
    } else if (s.x != null) {
      // Just position — new text at click location
      setCfg(c => ({ ...c, x: s.x, y: s.y, text: "" }));
    }
    seed.current = null; // consume
  }, [open, seed]);

  const handleCommit = useCallback(() => {
    if (!cfg.text.trim()) { onClose(); return; }
    onCommit(cfg);
    setCfg(c => ({ ...c, text: "" }));
    onClose();
  }, [cfg, onCommit, onClose]);

  if (!open) return null;

  return (
    <Modal onClose={onClose}>
      <div style={{ fontSize:12, fontWeight:700, color:"#ccc", marginBottom:12 }}>Add Text</div>
      <textarea
        value={cfg.text}
        onChange={e => setCfg(c => ({ ...c, text: e.target.value }))}
        placeholder="Type here..."
        rows={3}
        style={{ width:"100%", background:V.panelActive, border:`1px solid ${V.panelBorder2}`,
          borderRadius:4, color:V.text, padding:8, fontSize:14, fontFamily:cfg.font,
          resize:"vertical", marginBottom:8, outline:"none" }}
      />
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
        <select value={cfg.font} onChange={e => setCfg(c => ({ ...c, font: e.target.value }))}>
          {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <input type="number" value={cfg.size}
          onChange={e => setCfg(c => ({ ...c, size: +e.target.value }))}
          min={8} max={200} style={{ ...optInputStyle, width:50 }}/>
        <select value={cfg.weight} onChange={e => setCfg(c => ({ ...c, weight: e.target.value }))}>
          <option value="normal">Regular</option>
          <option value="bold">Bold</option>
          <option value="lighter">Light</option>
        </select>
        <select value={cfg.align} onChange={e => setCfg(c => ({ ...c, align: e.target.value }))}>
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
        <input type="color" value={cfg.color}
          onChange={e => setCfg(c => ({ ...c, color: e.target.value }))}
          style={{ width:32, height:26, border:`1px solid ${V.panelBorder2}`, borderRadius:3, cursor:"pointer" }}/>
      </div>
      <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
        <OptBtn onClick={onClose}>Cancel</OptBtn>
        <OptBtn accent onClick={handleCommit}>
          {activeTextData ? "Update Text" : "Add Text"}
        </OptBtn>
      </div>
    </Modal>
  );
}
